import React, { FC } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, Sector } from 'recharts';
import { Paper, Typography, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';

export const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const filteredPayload = payload.filter(item => item.payload.showInTooltip !== false);

    if (filteredPayload.length === 0) return null;

    return (
      <Paper>
        <Box p={2}>
          <Typography gutterBottom>{filteredPayload[0].name}</Typography>
          {filteredPayload.map((p, i) => (
            <Box key={i} display="flex" alignItems="center" style={{ width: '16ch' }}>
              <Typography variant="h4">{`${Number(p.value).toFixed(1)}%`}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }

  return null;
};

export const COLORS = ['#00A7F7', '#2B3C87'];

const renderPhysicianUtilizationActiveShape = (props, name, subtitle) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  const chartRadius = outerRadius - innerRadius;
  const primaryFontSize = Math.max(16, Math.min(28, chartRadius * 0.18));
  const secondaryFontSize = Math.max(14, Math.min(18, chartRadius * 0.12));

  return (
    <g>
      <text
        x={cx}
        y={cy}
        dy={subtitle ? -6 : 0}
        fontSize={primaryFontSize}
        fontWeight={400}
        textAnchor="middle"
        fill="white"
      >
        {name}
      </text>
      {subtitle && (
        <text
          x={cx}
          y={cy}
          dy={12}
          fontSize={secondaryFontSize}
          fontWeight={500}
          textAnchor="middle"
          fill="rgb(125	163	206)"
        >
          {subtitle}
        </text>
      )}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

const renderLegend = props => {
  const { payload } = props;

  const filteredPayload = payload.filter(entry => entry.payload.showInLegend !== false);

  return (
    <div style={{ textAlign: 'left', paddingLeft: 10 }}>
      {filteredPayload.map((entry, index) => (
        <span
          key={`legend-${index}`}
          style={{ color: entry.color, marginRight: 10, display: 'inline-flex', alignItems: 'center' }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              backgroundColor: entry.color,
              borderRadius: '50%',
              marginRight: 5,
            }}
          />
          {entry.value}
        </span>
      ))}
    </div>
  );
};

const PhysicianUtilizationChart: FC<{ value: number }> = ({ value }) => {
  const theme = useTheme();

  const utilization = value * 100;

  const data = [
    { name: 'Active Case Time', value: utilization, showInTooltip: true, showInLegend: true },
    { name: 'Rest', value: Math.max(0, 100 - utilization), showInTooltip: false, showInLegend: false },
  ];

  return (
    <ResponsiveContainer width="100%" height="100%" aspect={1.5 / 1}>
      <PieChart>
        <Pie
          startAngle={90}
          endAngle={-270}
          dataKey="value"
          data={data}
          innerRadius="60%"
          outerRadius="85%"
          paddingAngle={1}
          activeShape={props =>
            renderPhysicianUtilizationActiveShape(props, `${utilization.toFixed(1)}%`, 'Active Case Time')
          }
          activeIndex={0}
        >
          {data.map((entry, index) => (
            <Cell
              stroke="none"
              style={{ outline: 'none' }}
              key={`cell-${index}`}
              fill={COLORS[index % COLORS.length]}
            />
          ))}
          <Cell stroke="none" style={{ outline: 'none' }} fill="#00a7f7" />
        </Pie>
        <Tooltip
          content={({ active, payload, label }) => <CustomTooltip active={active} payload={payload} label={label} />}
          cursor={{ fill: 'rgba(255,255,255,0.15)' }}
          contentStyle={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 4,
            border: 0,
          }}
        />
        <Legend content={renderLegend} align="left" verticalAlign="bottom" />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default PhysicianUtilizationChart;
